import React from "react";
import Layout from "@/components/utu/Layout";
import { GlassCard, GoldPillButton } from "@/components/utu/atoms";
import { SEO } from "@/components/SEO";
import { Shield, Database, Lock, Cpu, ArrowRight } from "lucide-react";
import { BOOKING_URL } from "@/mock";

export default function CustomAIModels() {
  return (
    <Layout>
      <SEO
        title="Custom AI Models (Local/Private)"
        description="Fine-tune and deploy sovereign Small Language Models (SLMs) on private hardware. Secure Swahili, Sheng, and low-resource dialect training with strict data privacy."
        url="/services/custom-ai-models"
      />
      <section className="mx-auto max-w-5xl px-5 py-12 md:px-8">
        {/* Header Hero */}
        <div className="mb-16 text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/20 bg-amber-500/5 px-3 py-1 text-xs text-[hsl(var(--utu-gold))] backdrop-blur-xl mb-4">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--utu-gold))]" />
            Sovereign AI Infrastructure
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl md:text-6xl mb-6">
            Custom AI Models
          </h1>
          <p className="text-lg text-white/70 leading-relaxed">
            Deploy private, domain-specific AI models that execute entirely within your organization's security perimeter. No API calls, no third-party data collection, absolute compliance.
          </p>
        </div>

        {/* Feature Bento Grid */}
        <div className="grid gap-6 md:grid-cols-2 mb-16">
          <GlassCard className="p-8">
            <Lock className="h-8 w-8 text-[hsl(var(--utu-gold))] mb-4" />
            <h3 className="text-2xl font-bold text-white mb-3">On-Premises & Private VPN</h3>
            <p className="text-sm text-white/65 leading-relaxed">
              We fine-tune open-weights models (like Llama-3-8B and Qwen-2.5) to run natively on your client servers or secure VPC environments. Your private business databases and conversations never traverse public APIs or external networks.
            </p>
          </GlassCard>

          <GlassCard className="p-8">
            <Cpu className="h-8 w-8 text-[hsl(var(--utu-gold))] mb-4" />
            <h3 className="text-2xl font-bold text-white mb-3">Agglutinative Language Curation</h3>
            <p className="text-sm text-white/65 leading-relaxed">
              Standard commercial tokenizers are optimized for English, inflating API billing by 4x for Swahili and African dialects. We build custom vocabulary extensions and tokenizer maps to process agglutinative prefixes natively.
            </p>
          </GlassCard>

          <GlassCard className="p-8">
            <Database className="h-8 w-8 text-[hsl(var(--utu-gold))] mb-4" />
            <h3 className="text-2xl font-bold text-white mb-3">Dialect Curation & Sheng Support</h3>
            <p className="text-sm text-white/65 leading-relaxed">
              Our training sets incorporate colloquial structures, Sheng slang blends, and localized grammar paradigms. The resulting weights understand the cultural context and conversational flow of East African youth.
            </p>
          </GlassCard>

          <GlassCard className="p-8">
            <Shield className="h-8 w-8 text-[hsl(var(--utu-gold))] mb-4" />
            <h3 className="text-2xl font-bold text-white mb-3">Structured Safeguards</h3>
            <p className="text-sm text-white/65 leading-relaxed">
              We embed strict verification wrappers that validate generated outputs against institutional policy guidelines and legal frameworks. Keep hallucinations to zero in critical environments.
            </p>
          </GlassCard>
        </div>

        {/* Rationale & Hardware Specs */}
        <GlassCard className="p-8 md:p-12 mb-16">
          <h2 className="text-3xl font-bold text-white mb-6 text-center md:text-left">Private Hosting Architecture</h2>
          <div className="grid gap-8 md:grid-cols-3">
            <div className="space-y-2">
              <div className="text-xs font-semibold text-white/55 uppercase tracking-wider">Quantization</div>
              <div className="text-lg font-bold text-white">4-bit AWQ & GGUF</div>
              <p className="text-xs text-white/60">Optimized weights compression enabling 8B models to execute efficiently on accessible hardware without loss of accuracy.</p>
            </div>
            <div className="space-y-2">
              <div className="text-xs font-semibold text-white/55 uppercase tracking-wider">Inference Speed</div>
              <div className="text-lg font-bold text-white">&gt; 35 tokens / sec</div>
              <p className="text-xs text-white/60">Sub-second response delivery optimized via vLLM engine setups on single consumer-grade enterprise server cards.</p>
            </div>
            <div className="space-y-2">
              <div className="text-xs font-semibold text-white/55 uppercase tracking-wider">Security Alignment</div>
              <div className="text-lg font-bold text-white">Zero Cloud Ingress</div>
              <p className="text-xs text-white/60">Isolated execution wrappers keeping all inputs inside your air-gapped database or client network boundary.</p>
            </div>
          </div>
        </GlassCard>

        {/* Call to Action */}
        <div className="text-center">
          <h3 className="text-2xl font-bold text-white mb-4">Interested in Local Model Deployment?</h3>
          <p className="text-sm text-white/60 mb-8 max-w-xl mx-auto">
            Book an architecture audit to review your data systems and evaluate custom training viability on your current hardware infrastructure.
          </p>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
            <GoldPillButton className="h-12 px-8">
              Book Architecture Audit
              <ArrowRight className="h-4 w-4 ml-2" />
            </GoldPillButton>
          </a>
        </div>
      </section>
    </Layout>
  );
}
